import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { DATA_LAYER_EVENT, DATA_LAYER_LOGIN_STATE_OPTIONS, DataLayer_CheckAddress, DataLayer_Event, DataLayer_FormSubmission, DataLayer_LoginState, DataLayer_User, DATA_LAYER_FORM_SUBMISSION_ID_OPTIONS, DataLayer_Subscribe, DataLayer_ViewItemList, DataLayer_ViewItem, DataLayer_AddToCart, DataLayer_BeginCheckout, DataLayer_BannerClicked, DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS, DataLayer_SignUpFunnel_User, DataLayer_SignUpFunnel_Event } from './data-layer.types';
import { LocalStorageService } from '../local-storage.service';
import { isPlatformBrowser } from '@angular/common';
import { Session } from '../types/session.type';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class DataLayerService {

  #localStorageService = inject(LocalStorageService);

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  trackLoginEvent() {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_User>({
      event: DATA_LAYER_EVENT.login,
      user
    });
  }

  trackSignUpEvent() {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_User>({
      event: DATA_LAYER_EVENT.sign_up,
      user
    });
  }

  trackLoginStatusEvent() {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_LoginState>({
      event: DATA_LAYER_EVENT.login_state,
      data: {
        status: DATA_LAYER_LOGIN_STATE_OPTIONS.logged_in,
      },
      user
    });
  }

  trackCheckAddressEvent(delivery_area: boolean) {
    this.#trackEvent<DataLayer_CheckAddress>({
      event: DATA_LAYER_EVENT.check_address,
      data: {
        delivery_area
      }
    })
  }

  trackDeliveryAreaFormSubmissionEvent(user: DataLayer_User) {
    this.#trackEvent<DataLayer_FormSubmission>({
      event: DATA_LAYER_EVENT.form_submission,
      data: {
        form_id: DATA_LAYER_FORM_SUBMISSION_ID_OPTIONS.delivery_area_update,
      },
      user
    })
  }

  trackNewsletterFormSubmissionEvent(email: string) {
    this.#trackEvent<DataLayer_FormSubmission>({
      event: DATA_LAYER_EVENT.form_submission,
      data: {
        form_id: DATA_LAYER_FORM_SUBMISSION_ID_OPTIONS.newsletter,
      },
      user: { email }
    })
  }

  trackSubscribeEvent(data: DataLayer_Subscribe) {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_Subscribe>({
      event: DATA_LAYER_EVENT.subscribe,
      data,
      user
    })
  }

  trackViewItemListEvent(data: DataLayer_ViewItemList) {
    const user: DataLayer_User | undefined = this.#isTrackEventValid() || undefined;
    this.#trackEvent<DataLayer_ViewItemList>({
      event: DATA_LAYER_EVENT.view_item_list,
      data,
      user
    })
  }

  trackViewItemEvent(data: DataLayer_ViewItem) {
    const user: DataLayer_User | undefined = this.#isTrackEventValid() || undefined;
    this.#trackEvent<DataLayer_ViewItem>({
      event: DATA_LAYER_EVENT.view_item,
      data,
      user
    })
  }

  trackAddToCartEvent(data: DataLayer_AddToCart) {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_AddToCart>({
      event: DATA_LAYER_EVENT.add_to_cart,
      data,
      user
    })
  }

  trackBeginCheckoutEvent(data: DataLayer_BeginCheckout) {
    const user: DataLayer_User | null = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_BeginCheckout>({
      event: DATA_LAYER_EVENT.begin_checkout,
      data,
      user
    })
  }

  trackBannerClickedEvent(data: DataLayer_BannerClicked) {
    const user: DataLayer_User | null = this.#isTrackEventValid();

    const eventData: DataLayer_Event<DataLayer_BannerClicked> = {
      event: DATA_LAYER_EVENT.banner_click,
      data,
    }

    if (user) eventData.user = user;

    this.#trackEvent<DataLayer_BannerClicked>(eventData);
  }

  #trackEvent<T>(info: DataLayer_Event<T>) {
    try {
      if (!this.#isPlatformBrowser() || !environment.production) return;
      const payload: any = {
        event: info.event,
        ...info.data,
      }

      if (info.user)
        payload.user = info.user;

      window.dataLayer.push(payload);
    } catch (error) {
      console.log('Error Tracking DataLayer Event: ', info);
    }
  }

  #hasSession() {
    const currentSession: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    return !!(currentSession?.accountInfo?.id)
  }

  #isPlatformBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  #getUserInfo(): DataLayer_User | null {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session || !session.accountInfo) return null;
    return {
      email: session.accountInfo.email,
      first_name: session.accountInfo.firstName,
      last_name: session.accountInfo.lastName,
      phone: session.accountInfo.phone,
      user_id: session.accountInfo.id.toString()
    } as DataLayer_User
  }

  #isTrackEventValid(): DataLayer_User | null {
    if (!this.#hasSession()) return null;
    return this.#getUserInfo();
  }

  //#region Sign Up Funnel

  trackSignUpFunnelDeliveryAddressEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_address,
      user
    })
  }

  trackSignUpFunnelDeliveryAreaUpdateEvent(user: DataLayer_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_area_update,
      user
    })
  }

  trackSignUpFunnelAvailabilityEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.availability,
      user
    })
  }

  trackSignUpFunnelProduceBoxEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.produce_box,
      user
    })
  }

  trackSignUpFunnelSubscriptionsEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.subscriptions,
      user
    })
  }

  trackSignUpFunnelPersonalInfoEvent(user: DataLayer_SignUpFunnel_User, data?: { sms_optin: boolean, marketing_optin: boolean }) {
    let payload: DataLayer_SignUpFunnel_Event = {
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.personal_info,
      user
    }

    if (data) {
      payload.sms_optin = data.sms_optin;
      payload.marketing_optin = data.marketing_optin;
    }

    this.#trackSignUpFunnelEvent(payload);
  }

  trackSignupFunnelPaymentMethodEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up_funnel,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.payment_method,
      user
    })
  }

  trackSignUpFunnelAccountSuccessEvent(user: DataLayer_SignUpFunnel_User) {
    this.#trackSignUpFunnelEvent({
      event: DATA_LAYER_EVENT.sign_up,
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.account_success,
      user
    })
  }

  #trackSignUpFunnelEvent(info: DataLayer_SignUpFunnel_Event) {
    try {
      const stepsCompleted: { email: string, step_completed: string, event: string }[] = this.#localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_FUNNEL_STEPS_COMPLETED) || [];

      const stepCompleted = stepsCompleted.find(s => s.email === info.user.email && s.step_completed === info.funnel_step && s.event === info.event);
      if (stepCompleted) return;

      stepsCompleted.push({ email: info.user.email, step_completed: info.funnel_step, event: info.event });

      this.#localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_FUNNEL_STEPS_COMPLETED, stepsCompleted);

      if (!this.#isPlatformBrowser() || !environment.production) return;
      window.dataLayer.push(info);
    } catch (error) {
      console.log('Error Tracking DataLayer Sign Up Funnel Event: ', info);
    }
  }
  //#endregion

}